/* eslint-disable react/no-array-index-key */
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BiLogOut } from "react-icons/bi";

import { IMenuConf, adminMenu, juloaAdminMenu, proMenu } from "conf/menu";

import useAuth from "common/hooks/useAuth";
import useRouter from "common/hooks/use-router";
import getWording from "common/utils/wording";
import useEmergencies from "common/hooks/useEmergencies";

interface IMenu {
  currentId: string;
  setSidebarOpen?: any;
}

const menuStyle = "text-white hover:bg-slate-100 hover:bg-opacity-50";

function Menu({ currentId, setSidebarOpen }: IMenu) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser, signout } = useAuth();
  const { isEmergencyAlert } = useEmergencies();

  const renderMenu = (currentMenu: IMenuConf) => {
    return Object.keys(currentMenu).map((key: string, index: number) => {
      const { label, children } = currentMenu[key];

      const childrenList = children
        ?.filter(
          (child) => !child.isReservedForJuloa || currentUser?.isJuloaAdmin
        )
        .map(({ id, Icon, label: childLabel, useWordingPlural }) => {
          const isCurrent = currentId?.includes(id);
          const isEmergencies = id === "emergencies" && isEmergencyAlert;
          return (
            <Link
              key={id}
              to={`/${id}`}
              className={classNames(
                isCurrent
                  ? "bg-slate-100 bg-opacity-90 text-gray-900"
                  : isEmergencies
                  ? "bg-red-600 text-white font-semibold "
                  : menuStyle,
                "relative group flex items-center px-2 py-2 text-sm font-medium rounded-lg"
              )}
              onClick={setSidebarOpen ? () => setSidebarOpen(false) : () => {}}
            >
              <div
                className={`flex items-center justify-center ${
                  isCurrent ? "bg-cyan-700" : "bg-purple-800"
                } rounded-lg p-2 mr-3`}
              >
                <Icon
                  className={classNames(
                    isCurrent
                      ? "text-white font-bold"
                      : isEmergencies
                      ? "text-yellow-300"
                      : "text-white",
                    "flex-shrink-0 h-5 w-5"
                  )}
                  aria-hidden="true"
                />
              </div>
              {useWordingPlural
                ? t(`titlePages.${childLabel}`, {
                    wording: getWording(true, true),
                  })
                : t(`titlePages.${childLabel}`)}

              {!!isEmergencies && (
                <span className="absolute top-1 right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-200 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400" />
                </span>
              )}
            </Link>
          );
        });
      return (
        <div key={`menu-group${index}`}>
          <div
            className="mt-4 mb-2 text-sm text-white font-semibold"
            key={`menu-${index}`}
          >
            {t(`titleDividers.${label}`)}
          </div>
          {childrenList}
        </div>
      );
    });
  };

  const desktopScrollBar = `overflow-y-auto [&::-webkit-scrollbar]:w-1.5
  [&::-webkit-scrollbar-track]:rounded-full
  [&::-webkit-scrollbar-track]:bg-gray-100
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-teal-600
  dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500`;

  return (
    <div className="sidebar rounded-lg flex flex-col flex-1 h-full">
      <div className="flex flex-col px-12 pt-0 md:pt-6 pb-0 md:pb-1 ">
        <img
          src="/images/logo_blanc.png"
          className="object-cover mb-3"
          alt="Logo"
        />
      </div>

      <div
        className={`flex-1 px-4 pb-6
   
        ${desktopScrollBar}
        `}
      >
        {renderMenu(
          currentUser?.isJuloaAdmin
            ? juloaAdminMenu
            : currentUser?.isAdmin
            ? adminMenu
            : proMenu
        )}

        <button
          type="button"
          className="group flex items-center px-2 py-2 text-sm font-medium rounded-lg mt-2  text-white bg-slate-100 hover:bg-opacity-50 bg-opacity-20
        w-full "
          onClick={() => {
            signout();
            navigate("/login");
          }}
        >
          <div className="flex items-center justify-center rounded-lg py-1 px-2 mr-3">
            <BiLogOut
              className={classNames("text-white", "flex-shrink-0 h-5 w-5")}
              aria-hidden="true"
            />
          </div>
          {t("titlePages.logout")}
        </button>
      </div>
    </div>
  );
}

export default Menu;
